import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { lumi } from '@/lib/lumi'
import { useAuth } from '@/hooks/useAuth'
import {Flame, TrendingUp, History, ArrowLeft, Award} from 'lucide-react'


interface BurnMeter {
  _id: string
  currentAmount: number
  maxAmount: number
  totalBurned: number
  isActive: boolean
  lastResetDate: string
}

interface BurnHistory {
  _id: string
  burnAmount: number
  burnDate: string
  transactionProof: string
  resetBy: string
  notes?: string
}

function BurnDashboard() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [burnMeter, setBurnMeter] = useState<BurnMeter | null>(null)
  const [burnHistory, setBurnHistory] = useState<BurnHistory[]>([])
  const [loading, setLoading] = useState(true)
  const [resetLoading, setResetLoading] = useState(false)
  
  // Admin reset form
  const [burnAmount, setBurnAmount] = useState('')
  const [transactionProof, setTransactionProof] = useState('')
  const [notes, setNotes] = useState('')

  useEffect(() => {
    fetchBurnData()
    
    // Refresh data when window gains focus (user navigates back)
    const handleFocus = () => {
      fetchBurnData()
    }
    
    window.addEventListener('focus', handleFocus)
    return () => window.removeEventListener('focus', handleFocus)
  }, [])

  const fetchBurnData = async () => {
    try {
      setLoading(true)
      
      // Fetch burn meter
      const meterResult = await lumi.entities.burn_meter.list({ limit: 1 })
      if (meterResult?.list?.[0]) {
        setBurnMeter(meterResult.list[0])
      }
      
      // Fetch burn history
      const historyResult = await lumi.entities.burn_history.list({
        sort: { burnDate: -1 },
        limit: 20
      })
      if (historyResult?.list) {
        setBurnHistory(historyResult.list)
      }
    } catch (error) {
      console.error('Failed to fetch burn data:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleReset = async () => {
    if (!user || !burnMeter) return
    
    const amount = parseInt(burnAmount)
    if (isNaN(amount) || amount <= 0) {
      alert('Please enter a valid burn amount')
      return
    }
    
    if (!transactionProof.trim()) {
      alert('Please enter transaction proof')
      return
    }

    try {
      setResetLoading(true)
      const now = new Date().toISOString()
      
      // Create burn history record
      await lumi.entities.burn_history.create({
        burnAmount: amount,
        burnDate: now,
        transactionProof: transactionProof.trim(),
        resetBy: user.userId,
        notes: notes.trim() || '',
        creator: user.userId,
        createdAt: now,
        updatedAt: now
      })
      
      // Update burn meter
      await lumi.entities.burn_meter.update(burnMeter._id, {
        currentAmount: 0,
        isActive: true,
        totalBurned: burnMeter.totalBurned + amount,
        lastResetDate: now,
        updatedAt: now
      })
      
      // Clear form
      setBurnAmount('')
      setTransactionProof('')
      setNotes('')
      
      // Refresh data
      await fetchBurnData()
      
      alert('✅ Burn recorded and meter reset successfully!')
    } catch (error) {
      console.error('Failed to reset meter:', error)
      alert('❌ Failed to reset meter. Please try again.')
    } finally {
      setResetLoading(false)
    }
  }

  const percentage = burnMeter ? (burnMeter.currentAmount / burnMeter.maxAmount) * 100 : 0
  const isFull = burnMeter && burnMeter.currentAmount >= burnMeter.maxAmount

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#1a1a3e] via-[#2d1b4e] to-[#1a1a3e] flex items-center justify-center">
        <p className="text-white text-xl">Loading burn data...</p>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0a0118] via-[#0f0520] to-[#1a0a2e] relative overflow-hidden">
      {/* Stars background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(150)].map((_, i) => (
          <div
            key={i}
            className="absolute bg-white rounded-full animate-pulse"
            style={{
              width: Math.random() * 3 + 0.5 + 'px',
              height: Math.random() * 3 + 0.5 + 'px',
              left: Math.random() * 100 + '%',
              top: Math.random() * 100 + '%',
              animationDelay: Math.random() * 3 + 's',
              animationDuration: Math.random() * 2 + 2 + 's',
              opacity: Math.random() * 0.7 + 0.3,
              boxShadow: `0 0 ${Math.random() * 4 + 2}px rgba(255,255,255,${Math.random() * 0.6})`
            }}
          />
        ))}
      </div>

      {/* Nebula effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-20">
        <div className="absolute w-96 h-96 rounded-full blur-3xl bg-purple-500/50 left-1/4 top-1/4 animate-pulse" style={{ animationDuration: '8s' }} />
        <div className="absolute w-80 h-80 rounded-full blur-3xl bg-blue-500/40 right-1/4 bottom-1/4 animate-pulse" style={{ animationDuration: '10s' }} />
      </div>

      {/* Content */}
      <div className="relative z-10 container mx-auto px-4 py-8 max-w-6xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 px-3 md:px-4 py-2 bg-white hover:bg-gray-100 text-black font-bold text-sm md:text-base rounded-lg transition-all border-2 border-black"
          >
            Menu
          </button>
          
          <h1 className="text-2xl md:text-5xl font-black text-white flex items-center gap-2 md:gap-3">
            <Flame className="text-red-500" size={32} />
            BURN DASHBOARD
          </h1>
          
          <button
            onClick={() => navigate('/leaderboard')}
            className="flex items-center gap-2 px-3 md:px-4 py-2 bg-yellow-500 hover:bg-yellow-600 text-black font-bold text-sm md:text-base rounded-lg transition-all border-2 border-white"
          >
            <Award size={18} />
            Leaders
          </button>
        </div>

        {/* Burn Meter Display */}
        <div className="bg-black/70 backdrop-blur-xl border-2 border-purple-500/50 rounded-2xl shadow-[0_0_60px_rgba(168,85,247,0.3)] p-4 md:p-8 mb-6 md:mb-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 mb-6">
            <h2 className="text-xl md:text-3xl font-black text-white flex items-center gap-2 md:gap-3">
              <Flame className="text-red-500" size={28} />
              Current Burn Meter
            </h2>
            {isFull && (
              <span className="px-3 py-2 md:px-4 bg-red-600 text-white font-black text-sm md:text-base rounded-lg animate-pulse">
                🔒 METER FULL - GAMEPLAY LOCKED
              </span>
            )}
          </div>
          
          {/* Progress Bar */}
          <div className="relative w-full h-12 md:h-16 bg-gray-900 rounded-full overflow-hidden mb-4 border-2 border-purple-500/50">
            <div
              className={`h-full transition-all duration-1000 ${
                isFull ? 'bg-gradient-to-r from-red-500 via-red-600 to-red-700' : 'bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500'
              }`}
              style={{ width: `${Math.min(percentage, 100)}%` }}
            >
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-lg md:text-3xl font-black text-white drop-shadow-lg">
                  {burnMeter?.currentAmount.toLocaleString()} / {burnMeter?.maxAmount.toLocaleString()} 🔥
                </span>
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4 mt-4 md:mt-6">
            <div className="bg-black/80 backdrop-blur-sm border-2 border-yellow-500/70 rounded-xl p-3 md:p-4 text-center shadow-[0_0_20px_rgba(234,179,8,0.3)]">
              <p className="text-xs md:text-sm text-yellow-400 font-bold mb-1">PENDING BURN</p>
              <p className="text-2xl md:text-3xl font-black text-yellow-400">{burnMeter?.currentAmount.toLocaleString()}</p>
              <p className="text-xs text-gray-400 mt-1">tokens waiting</p>
            </div>
            
            <div className="bg-black/80 backdrop-blur-sm border-2 border-red-500/70 rounded-xl p-3 md:p-4 text-center shadow-[0_0_20px_rgba(239,68,68,0.3)]">
              <p className="text-xs md:text-sm text-red-400 font-bold mb-1">TOTAL BURNED</p>
              <p className="text-2xl md:text-3xl font-black text-red-400">{burnMeter?.totalBurned.toLocaleString()}</p>
              <p className="text-xs text-gray-400 mt-1">all time</p>
            </div>
            
            <div className="bg-black/80 backdrop-blur-sm border-2 border-purple-500/70 rounded-xl p-3 md:p-4 text-center shadow-[0_0_20px_rgba(168,85,247,0.3)]">
              <p className="text-xs md:text-sm text-purple-400 font-bold mb-1">METER STATUS</p>
              <p className="text-2xl md:text-3xl font-black text-white">{percentage.toFixed(1)}%</p>
              <p className="text-xs text-gray-400 mt-1">capacity</p>
            </div>
          </div>
          
          {burnMeter?.lastResetDate && (
            <p className="text-xs md:text-sm text-gray-300 text-center mt-3 md:mt-4">
              Last reset: {new Date(burnMeter.lastResetDate).toLocaleString()}
            </p>
          )}
        </div>

        {/* Admin Reset Section */}
        {user?.userRole === 'ADMIN' && user?.userId === 'SPE2036100314694868992' && (
          <div className="bg-black/70 backdrop-blur-xl border-2 border-purple-500/50 rounded-2xl shadow-[0_0_60px_rgba(168,85,247,0.3)] p-4 md:p-8 mb-6 md:mb-8">
            <h2 className="text-xl md:text-3xl font-black text-yellow-400 mb-4 md:mb-6 flex items-center gap-2 md:gap-3">
              <Flame className="text-red-500" size={28} />
              🔐 ADMIN: Record Burn & Reset Meter
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4 mb-3 md:mb-4">
              <div>
                <label className="block text-white font-bold text-sm md:text-base mb-2">Burn Amount (tokens)</label>
                <input
                  type="number"
                  value={burnAmount}
                  onChange={(e) => setBurnAmount(e.target.value)}
                  className="w-full px-3 md:px-4 py-2 md:py-3 rounded-lg bg-black/50 border-2 border-purple-500/30 text-white font-bold text-base md:text-lg focus:outline-none focus:border-purple-500 transition-all duration-300"
                  placeholder="Enter burn amount"
                />
              </div>
              
              <div>
                <label className="block text-white font-bold text-sm md:text-base mb-2">Transaction Proof (hash/link)</label>
                <input
                  type="text"
                  value={transactionProof}
                  onChange={(e) => setTransactionProof(e.target.value)}
                  className="w-full px-3 md:px-4 py-2 md:py-3 rounded-lg bg-black/50 border-2 border-purple-500/30 text-white font-bold text-sm md:text-base focus:outline-none focus:border-purple-500 transition-all duration-300"
                  placeholder="0x..."
                />
              </div>
            </div>
            
            <div className="mb-3 md:mb-4">
              <label className="block text-white font-bold text-sm md:text-base mb-2">Notes (optional)</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="w-full px-3 md:px-4 py-2 md:py-3 rounded-lg bg-black/50 border-2 border-purple-500/30 text-white font-bold text-sm md:text-base focus:outline-none focus:border-purple-500 transition-all duration-300"
                placeholder="Additional notes about this burn..."
                rows={2}
              />
            </div>
            
            <button
              onClick={handleReset}
              disabled={resetLoading}
              className="w-full px-4 md:px-8 py-3 md:py-4 bg-gradient-to-r from-red-600 via-red-700 to-red-600 hover:from-red-700 hover:via-red-800 hover:to-red-700 text-white text-base md:text-xl font-black rounded-xl shadow-[0_0_30px_rgba(220,38,38,0.5)] hover:shadow-[0_0_50px_rgba(220,38,38,0.8)] active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed border-2 border-red-400"
            >
              {resetLoading ? '⏳ PROCESSING...' : '🔥 RECORD BURN & RESET METER 🔥'}
            </button>
          </div>
        )}

        {/* Burn History */}
        <div className="bg-black/70 backdrop-blur-xl border-2 border-purple-500/50 rounded-2xl shadow-[0_0_60px_rgba(168,85,247,0.3)] p-4 md:p-8 mb-6 md:mb-8">
          <h2 className="text-xl md:text-3xl font-black text-white mb-4 md:mb-6 flex items-center gap-2 md:gap-3">
            <History className="text-yellow-400" size={28} />
            Burn History
          </h2>
          
          {burnHistory.length === 0 ? (
            <p className="text-center text-gray-400 py-6 md:py-8">No burns recorded yet</p>
          ) : (
            <div className="space-y-3 md:space-y-4">
              {burnHistory.map((burn) => (
                <div
                  key={burn._id}
                  className="bg-gray-900/80 backdrop-blur-sm rounded-xl p-3 md:p-4 border-2 border-purple-500/30 hover:border-purple-500 transition-all"
                >
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                    <div className="flex items-center gap-2 md:gap-3">
                      <Flame className="text-red-500" size={20} />
                      <div>
                        <p className="text-lg md:text-xl font-black text-red-500">
                          {burn.burnAmount.toLocaleString()} $RBunny
                        </p>
                        <p className="text-xs md:text-sm text-gray-400">
                          {new Date(burn.burnDate).toLocaleString()}
                        </p>
                      </div>
                    </div>
                    
                    <div className="md:text-right">
                      <p className="text-xs text-gray-400 font-bold mb-1">Transaction Proof:</p>
                      <a
                        href={burn.transactionProof.startsWith('http') ? burn.transactionProof : `#${burn.transactionProof}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-yellow-400 hover:text-yellow-300 font-mono text-xs break-all"
                      >
                        {burn.transactionProof.length > 30 
                          ? `${burn.transactionProof.slice(0, 15)}...${burn.transactionProof.slice(-15)}`
                          : burn.transactionProof}
                      </a>
                      {burn.notes && (
                        <p className="text-xs text-gray-400 mt-1 italic">{burn.notes}</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Info Section */}
        <div className="bg-black/70 backdrop-blur-xl border-2 border-purple-500/50 rounded-2xl shadow-[0_0_60px_rgba(168,85,247,0.3)] p-4 md:p-8 mt-6 md:mt-8">
          <h3 className="text-xl md:text-2xl font-black text-white mb-3 md:mb-4 flex items-center gap-2 md:gap-3">
            <TrendingUp className="text-yellow-400" size={24} />
            How Play-2-Burn Works
          </h3>
          <div className="space-y-2 md:space-y-3 text-white text-sm md:text-base">
            <p className="flex items-start gap-2">
              <span className="text-yellow-400 font-black">1.</span>
              <span>Must be logged in to contribute to the burn meter</span>
            </p>
            <p className="flex items-start gap-2">
              <span className="text-yellow-400 font-black">2.</span>
              <span>Each moon collected in-game equals 1 $RBunny token pending burn</span>
            </p>
            <p className="flex items-start gap-2">
              <span className="text-yellow-400 font-black">3.</span>
              <span>The burn meter tracks up to 25,000 tokens</span>
            </p>
            <p className="flex items-start gap-2">
              <span className="text-yellow-400 font-black">4.</span>
              <span>When meter reaches 25,000, logged-in users cannot play until tokens are burned</span>
            </p>
            <p className="flex items-start gap-2">
              <span className="text-yellow-400 font-black">5.</span>
              <span>Admin burns the tokens and resets the meter back to zero</span>
            </p>
            <p className="flex items-start gap-2">
              <span className="text-yellow-400 font-black">6.</span>
              <span>All burns are permanently recorded in the history for transparency</span>
            </p>
          </div>
        </div>
      </div>

    </div>
  )
}

export default BurnDashboard
